import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch } from 'react-redux';
import { setActiveUserInfo, setAuthToken } from '../../redux/features/app/appSlice';
import { AuthHelper } from '../auth/AuthHelper';
import { useAppSelector } from '../../redux/app/hooks';
import useAuthGuard from './useAuthGuard';
import { hostContextStorage } from '../hostContextStorage';
import { OidcAuthHelper } from '../auth/OidcAuthHelper';

export const useAuthSetup = () => {
    const Auth = useAuth();
    const authGuard = useAuthGuard();
    const dispatch = useDispatch();
    // keep a ref if we did autologin
    const didAutoLoginRef = useRef(false);
    const { embeddedMode } = useAppSelector((state) => state.app);
    const storage = hostContextStorage.autoMode();

    // Add this outside the hook to maintain across renders
    let currentSignInPromise: Promise<void> | null = null;

    const handleSilentSignIn = async () => {
        // If we're already authenticating, don't start another one; this might happen if we've left the current session to go on a redirect-hunt; thus loosing all refs and global constants in the process with the result of having two auth flows running at the same time - which in some cases will confuse the remote IdP
        if (authGuard.isAuthenticating()) {
            return;
        }
        authGuard.startAuth();

        // If there's already a sign-in in progress, wait for it
        if (currentSignInPromise) {
            console.log('Silent signin already in progress');
            return currentSignInPromise;
        }
        if (didAutoLoginRef.current) {
            console.log('Silent signin already did autologin');
            return;
        }
        didAutoLoginRef.current = true;
        console.log('Silent signin start');

        const storedHostContext = storage.load();
        if (storedHostContext?.id) {
            // Need to make sure we store the provider, otherwise the callback will load the wrong provider config
            OidcAuthHelper.persistProviderId(storedHostContext.id);
        }
        // Create new promise and store it
        currentSignInPromise = (async () => {
            try {
                console.log('handleSilentSignIn start');

                const user = await Auth.signinSilent();
                console.log('Silent signin', user);
                if (!user) {
                    await Auth.signinRedirect({});
                }
            } catch (error) {
                console.error('Error during silent signin:', error);
                await Auth.signinRedirect({});
            } finally {
                // Clear the promise when done
                currentSignInPromise = null;
            }
        })();

        return currentSignInPromise;
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (!Auth.isAuthenticated && !(window.location.pathname === '/' && urlParams.get('code'))) {
            const lastlogin = localStorage.getItem('oidcAutoLastLogin');
            console.log(
                'Auto login: isAuthenticated, pathname, lastlogin',
                Auth.isAuthenticated,
                window.location.pathname,
                lastlogin,
            );
            const shouldAutoLogin =
                embeddedMode || !lastlogin || new Date().getTime() - new Date(lastlogin).getTime() >= 30000;

            if (shouldAutoLogin) {
                console.log('Should auto login');
                localStorage.setItem('oidcAutoLastLogin', new Date().toISOString());
                void handleSilentSignIn();
            }
        } else {
            console.log('Not auto login', Auth.isAuthenticated, window.location);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Auth.isAuthenticated, window.location.href, embeddedMode]);

    useEffect(() => {
        dispatch(setAuthToken(Auth.user?.access_token ?? null));
        if (Auth.isAuthenticated && Auth.user) {
            dispatch(
                setActiveUserInfo({
                    id: Auth.user.profile.sub,
                    email: Auth.user.profile.email ?? '',
                    username: Auth.user.profile.name ?? '',
                    sessionId: AuthHelper.getSessionIdFromToken(Auth.user.access_token),
                }),
            );
        }
    }, [Auth, dispatch]);

    useEffect(() => {
        if (Auth.isAuthenticated) {
            AuthHelper.setLogoutFunction(async () => {
                try {
                    localStorage.removeItem('oidcAutoLastLogin');
                    await Auth.removeUser();
                    await Auth.clearStaleState();
                    const url = new URL(window.location.href);
                    url.searchParams.set('loggedout', 'true');
                    window.location.href = url.toString();
                } catch (error) {
                    console.error('Error during logout:', error);
                }
            });
            localStorage.removeItem('oidcAutoLastLogin');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Auth.isAuthenticated]);

    // Front-channel logout: the IdP loads /oidc-logout.html in a hidden iframe which clears
    // localStorage and broadcasts this message to all open tabs on the same origin.
    useEffect(() => {
        if (!Auth.isAuthenticated) {
            return;
        }

        let channel: BroadcastChannel;
        try {
            channel = new BroadcastChannel('oidc-logout');
        } catch {
            // BroadcastChannel not supported; front-channel logout notifications won't be received
            return;
        }

        channel.onmessage = (event: MessageEvent<{ iss: string | null; sid: string | null }>) => {
            // If the message carries an iss, verify it matches this tab's provider before
            // clearing state. This matters when both providers are in use concurrently in
            // different tabs: a logout from one provider should not clear the other.
            const { iss } = event.data;
            if (iss) {
                const currentAuthority = Auth.settings.authority.replace(/\/$/, '');
                const messageIss = iss.replace(/\/$/, '');
                if (currentAuthority !== messageIss) {
                    return;
                }
            }
            void (async () => {
                try {
                    await Auth.removeUser();
                    await Auth.clearStaleState();
                    // Auth.isAuthenticated becomes false, which causes the auto-login useEffect
                    // above to fire. From there the user will see the login screen and can
                    // sign in again explicitly. We intentionally avoid an immediate silent
                    // re-auth here: the IdP just told us the session ended, so a prompt=none
                    // request is likely to fail or produce unexpected results depending on
                    // timing, and the redirect would be disruptive in an iframe context.
                } catch (error) {
                    console.error('Front-channel logout: error clearing session:', error);
                }
            })();
        };

        return () => {
            channel.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Auth.isAuthenticated]);
};
